// SCR-230 and SCR-231 as library screens (ADR-101). The quotas list of one org, grouped by scope
// (org, teams, members, people), with each cap's measure, period, limit, enforcement, fail mode,
// status and owner; a row opens its summary with Edit, Disable/Enable, Delete and Grants (the
// quota's own page). That page is SCR-231: the quota's facts, its standing per scope instance, its
// grants and its history. Guardrail (platform-owned) and deployment-pinned quotas are read-only to
// whoever may not change them, and their actions are absent.
//
// The org and the money the viewer may see come from the org context (TASK-240), the principal
// from the scope the shell provides, and the way to a quota's page from the host (`useHost()`):
// the AllCode admin's customer routes, or the proxy Admin area's own under its base path. The API
// answers 501 for these operations until their handlers land; every read shows that as "not
// available yet".
import { useEffect, useState } from "preact/hooks";
import { useApi } from "../../api/context";
import type { ApiError } from "../../api/errors";
import type { OrgContext } from "../../api/org-context";
import { useHost } from "../../host/host";
import { useReadOnly } from "../../host/readonly";
import { ConfirmDialog } from "../../primitives/dialog";
import { StaleNote } from "../../primitives/stale-note";
import { Card, Chip } from "../../primitives/page";
import { RowDetail } from "../../primitives/row-detail";
import { Empty, ErrorBanner, Loading, NotFound } from "../../primitives/states";
import { DataTable, type Column } from "../../primitives/table";
import type { ScopeMe } from "../../scope/scope";
import { GrantsCard } from "./grant-dialog";
import { quotaHref } from "./hrefs";
import { QuotaEditor } from "./quota-editor";
import { EventsList, StandingPanel } from "./standing";
import {
  asApiError,
  bpPercent,
  canCreate,
  deleteQuota,
  loadable,
  loadQuota,
  loadQuotas,
  loadTargets,
  NOT_AVAILABLE,
  quotaRow,
  quotaRows,
  QUOTAS_TITLE,
  toggleStatus,
  writeAccess,
  writeFailure,
  type EditorMode,
  type Loadable,
  type Quota,
  type QuotaRow,
  type Targets,
  type WriteAccess,
} from "./model";

/** What the shell may learn from a screen: the title it shows (the quota's name once it is read). */
export type OnTitle = (title: string) => void;

const NO_TARGETS: Targets = { teams: [], users: [] };

/** The org's teams and people, for the pickers; an unreadable list leaves the pickers empty. */
function useTargets(orgId: string | undefined): Targets {
  const client = useApi();
  const [targets, setTargets] = useState<Targets>(NO_TARGETS);
  useEffect(() => {
    setTargets(NO_TARGETS);
    if (!orgId) return;
    loadTargets(client, orgId)
      .then(setTargets)
      .catch(() => setTargets(NO_TARGETS));
  }, [client, orgId]);
  return targets;
}

const COLUMNS: readonly Column<QuotaRow>[] = [
  {
    key: "name",
    header: "Quota",
    cell: (row) => (
      <span class="stack-cell">
        <span>{row.name}</span>
        <span class="cell-sub quota-scope">{row.scopeText}</span>
      </span>
    ),
  },
  { key: "metric", header: "Measure", help: "quotas.metric", cell: (row) => row.metric },
  { key: "period", header: "Period", help: "quotas.period", cell: (row) => row.period },
  { key: "limit", header: "Limit", help: "quotas.limit", numeric: true, cell: (row) => row.limit },
  { key: "enforcement", header: "When reached", help: "quotas.enforcement", cell: (row) => row.enforcement },
  { key: "failMode", header: "If unchecked", help: "quotas.failMode", cell: (row) => row.failMode },
  { key: "status", header: "Status", help: "quotas.status", cell: (row) => <Chip tone={row.statusTone}>{row.statusLabel}</Chip> },
  {
    key: "owner",
    header: "Owner",
    help: "quotas.owner",
    cell: (row) => (
      <span class="quota-badges">
        <Chip tone={row.ownerTone}>{row.ownerLabel}</Chip>
        {row.sourceLabel && <Chip tone="grey">{row.sourceLabel}</Chip>}
        {row.warnings.length > 0 && (
          <Chip tone="amber" title={row.warnings.join(" ")}>
            Check
          </Chip>
        )}
      </span>
    ),
  },
];

type Pending = { kind: "delete" | "toggle"; row: QuotaRow } | null;

export interface QuotasTabProps {
  context: OrgContext;
  me: ScopeMe;
  onTitle?: OnTitle | undefined;
}

/** The quotas of one org (SCR-230): the list, its row summaries, and the editor. */
export function QuotasTab({ context, me, onTitle }: QuotasTabProps) {
  const client = useApi();
  const host = useHost();
  const { readOnly, reason } = useReadOnly();
  const targets = useTargets(context.orgId);
  const [state, setState] = useState<Loadable<Quota[]>>({ kind: "loading" });
  const [open, setOpen] = useState<QuotaRow | null>(null);
  const [editor, setEditor] = useState<EditorMode | null>(null);
  const [pending, setPending] = useState<Pending>(null);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<ApiError | null>(null);

  const access = writeAccess(me);
  const load = () => {
    setState({ kind: "loading" });
    void loadable(() => loadQuotas(client, context.orgId)).then(setState);
  };
  useEffect(load, [client, context.orgId]);
  useEffect(() => onTitle?.(QUOTAS_TITLE), [onTitle]);

  const closeAll = () => {
    setPending(null);
    setOpen(null);
    setFailure(null);
  };
  const act = () => {
    if (!pending) return;
    const quota = pending.row.quota;
    setBusy(true);
    setFailure(null);
    const write = pending.kind === "delete" ? deleteQuota(client, quota) : toggleStatus(client, quota);
    write
      .then(() => {
        closeAll();
        load();
      })
      .catch((error: unknown) => setFailure(asApiError(error)))
      .finally(() => setBusy(false));
  };

  const goToQuota = (quota: Quota) => host.navigate(quotaHref(host, context.orgId, quota.id));
  const rows = state.kind === "loaded" ? quotaRows(state.value, context.moneyPolicy, access) : [];
  const newButton = canCreate(access) && state.kind === "loaded" && (
    <button type="button" class="button button-primary" disabled={readOnly} title={readOnly ? reason ?? "" : undefined} aria-describedby={readOnly ? "quotas-stale-note" : undefined} onClick={() => setEditor({ kind: "create" })}>
      New quota
    </button>
  );

  return (
    <div class="stack">
      {readOnly && <StaleNote id="quotas-stale-note" />}
      <Card title="Quotas" actions={newButton}>
        {state.kind === "unavailable" ? (
          <Empty message={NOT_AVAILABLE} />
        ) : (
          <DataTable
            caption="Quotas"
            hideCaption
            columns={COLUMNS}
            rows={rows}
            rowKey={(row) => row.id}
            loading={state.kind === "loading"}
            error={state.kind === "error" ? state.error : null}
            onRetry={load}
            emptyMessage="No limits yet — start with a team or each member."
            emptyAction={newButton}
            onRowClick={setOpen}
            rowLabel={(row) => `Open the quota ${row.name}`}
          />
        )}
      </Card>
      {state.kind === "loaded" && state.value.length > 0 && (
        <>
          <StandingPanel context={context} />
          <EventsList context={context} quotas={state.value} />
        </>
      )}
      {open && !pending && (
        <QuotaSummary
          row={open}
          context={context}
          onClose={closeAll}
          onEdit={() => {
            setEditor({ kind: "edit", quota: open.quota });
            setOpen(null);
          }}
          onToggle={() => setPending({ kind: "toggle", row: open })}
          onDelete={() => setPending({ kind: "delete", row: open })}
          onGrants={() => goToQuota(open.quota)}
        />
      )}
      {pending && (
        <ConfirmDialog
          title={pending.kind === "delete" ? `Delete ${pending.row.name}?` : `${pending.row.quota.status === "active" ? "Disable" : "Enable"} ${pending.row.name}?`}
          open
          message={
            <>
              <p>{pending.kind === "delete" ? "The quota, its grants and its standing are removed; its history stays." : pending.row.quota.status === "active" ? "Nothing is counted against this cap or refused by it until it is enabled again." : "The cap is counted and enforced again from now."}</p>
              {failure && <p role="alert">{writeFailure(failure)}</p>}
            </>
          }
          confirmLabel={pending.kind === "delete" ? "Delete" : pending.row.quota.status === "active" ? "Disable" : "Enable"}
          {...(pending.kind === "delete" ? { typedName: pending.row.name } : {})}
          busy={busy}
          onConfirm={act}
          onClose={closeAll}
        />
      )}
      {editor && (
        <QuotaEditor
          key={editor.kind === "edit" ? `${editor.quota.id}:${editor.quota.version}` : "new"}
          context={context}
          access={access}
          mode={editor}
          targets={targets}
          open
          onClose={() => setEditor(null)}
          onSaved={() => {
            setEditor(null);
            load();
          }}
          onReload={() => {
            if (editor.kind !== "edit") return;
            loadQuota(client, context.orgId, editor.quota.id)
              .then((quota) => setEditor({ kind: "edit", quota }))
              .catch(() => setEditor(null));
          }}
        />
      )}
    </div>
  );
}

function quotaFacts(row: QuotaRow, quota: Quota, staff: boolean) {
  const facts = [
    { label: "Applies to", value: row.scopeText },
    { label: "Measure", value: row.metric },
    { label: "Period", value: row.period },
    { label: "Limit", value: row.limit },
    { label: "Warn at", value: quota.thresholdsBp.length === 0 ? "No warnings" : quota.thresholdsBp.map(bpPercent).join(", ") },
    { label: "When reached", value: row.enforcement },
    { label: "Grace", value: bpPercent(quota.graceBp) },
    { label: "If the proxy cannot check", value: row.failMode },
    { label: "Status", value: <Chip tone={row.statusTone}>{row.statusLabel}</Chip> },
  ];
  if (staff) facts.push({ label: "Owner", value: row.ownerLabel }, { label: "Changed by", value: quota.updatedBy });
  return facts;
}

function QuotaSummary({
  row,
  context,
  onClose,
  onEdit,
  onToggle,
  onDelete,
  onGrants,
}: {
  row: QuotaRow;
  context: OrgContext;
  onClose(): void;
  onEdit(): void;
  onToggle(): void;
  onDelete(): void;
  onGrants(): void;
}) {
  const writable = row.readOnly === null;
  return (
    <RowDetail
      title={row.name}
      open
      onClose={onClose}
      class="dialog-wide"
      facts={quotaFacts(row, row.quota, context.surface === "staff")}
      actions={
        <>
          <button type="button" class="button button-secondary" onClick={onGrants}>
            Grants and standing
          </button>
          {writable && (
            <>
              <button type="button" class="button button-secondary" onClick={onToggle}>
                {row.quota.status === "active" ? "Disable" : "Enable"}
              </button>
              <button type="button" class="button button-danger" onClick={onDelete}>
                Delete
              </button>
              <button type="button" class="button button-primary" onClick={onEdit}>
                Edit
              </button>
            </>
          )}
        </>
      }
    >
      {row.readOnly && <p class="banner banner-info">{row.readOnly}</p>}
      {row.warnings.map((warning) => (
        <p key={warning} class="banner banner-warning">
          {warning}
        </p>
      ))}
    </RowDetail>
  );
}

export interface QuotaDetailProps {
  context: OrgContext;
  me: ScopeMe;
  quotaId: string;
  onTitle?: OnTitle | undefined;
}

/** One quota (SCR-231): its facts, standing, grants and history. */
export function QuotaDetail({ context, me, quotaId, onTitle }: QuotaDetailProps) {
  const client = useApi();
  const targets = useTargets(context.orgId);
  const [state, setState] = useState<Loadable<Quota>>({ kind: "loading" });
  const load = () => {
    setState({ kind: "loading" });
    void loadable(() => loadQuota(client, context.orgId, quotaId)).then(setState);
  };
  useEffect(load, [client, context.orgId, quotaId]);
  const name = state.kind === "loaded" ? state.value.name : null;
  useEffect(() => {
    if (name !== null) onTitle?.(name);
  }, [name, onTitle]);

  const access: WriteAccess = writeAccess(me);
  if (state.kind === "loading") return <Loading label="Loading the quota…" />;
  if (state.kind === "unavailable") return <Empty message={NOT_AVAILABLE} />;
  if (state.kind === "error") return state.error.status === 404 ? <NotFound /> : <ErrorBanner error={state.error} onRetry={load} />;

  const quota = state.value;
  const row = quotaRow(quota, context.moneyPolicy, access);
  return (
    <div class="stack">
      <Card title={quota.name}>
        {row.readOnly && <p class="banner banner-info">{row.readOnly}</p>}
        <dl class="row-detail-facts">
          {quotaFacts(row, quota, context.surface === "staff").map((fact) => (
            <div key={fact.label}>
              <dt>{fact.label}</dt>
              <dd>{fact.value}</dd>
            </div>
          ))}
        </dl>
      </Card>
      <StandingPanel context={context} quotaId={quota.id} />
      <GrantsCard context={context} quota={quota} users={targets.users} canWrite={row.readOnly === null} />
      <EventsList context={context} quotas={[quota]} quotaId={quota.id} />
    </div>
  );
}
